import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckIcon, XIcon } from 'lucide-react';
import { format } from 'date-fns';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch {
    return dateString;
  }
};

const BooleanField = ({ label, value, showAsText = false }) => {
  if (showAsText) {
    return (
      <div className="flex justify-between items-center py-2 border-b border-gray-100">
        <span className="font-medium text-gray-700">{label}:</span>
        <span className="text-gray-900">{value ? 'Tak' : 'Nie'}</span>
      </div>
    );
  }

  return (
    <div className="flex justify-between items-center py-2 border-b border-gray-100">
      <span className="font-medium text-gray-700">{label}:</span>
      {value ? (
        <Badge variant="default" className="bg-green-500">
          <CheckIcon className="w-3 h-3 mr-1" />
          Tak
        </Badge>
      ) : (
        <Badge variant="secondary" className="bg-gray-500">
          <XIcon className="w-3 h-3 mr-1" />
          Nie
        </Badge>
      )}
    </div>
  );
};

const TextField = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-100">
    <span className="font-medium text-gray-700">{label}:</span>
    <span className="text-gray-900">{value || 'N/A'}</span>
  </div>
);

const DateField = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b border-gray-100">
    <span className="font-medium text-gray-700">{label}:</span>
    <span className="text-gray-900">{formatDate(value)}</span>
  </div>
);

const PeriodField = ({ label, period }) => (
  <div className="py-2 border-b border-gray-100">
    <span className="font-medium text-gray-700">{label}:</span>
    <div className="ml-4 mt-1">
      <div className="flex justify-between">
        <span className="text-sm text-gray-600">Od:</span>
        <span className="text-gray-900">{formatDate(period?.from)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm text-gray-600">Do:</span>
        <span className="text-gray-900">{formatDate(period?.to)}</span>
      </div>
    </div>
  </div>
);

export const KwestionariushView = ({ kwestionariush }) => {
  const { t } = useTranslation();

  if (!kwestionariush || !kwestionariush.data) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Brak danych kwestionariusza</p>
      </div>
    );
  }

  const { personalInfo, declarations } = kwestionariush.data;

  const getStatusBadge = (status) => {
    const statusConfig = {
      DRAFT: { label: 'Szkic', color: 'bg-yellow-500' },
      SUBMITTED: { label: 'Przesłany', color: 'bg-blue-500' },
      APPROVED: { label: 'Zatwierdzony', color: 'bg-green-500' },
      REJECTED: { label: 'Odrzucony', color: 'bg-red-500' }
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
    
    return (
      <Badge className={`${config.color} text-white`}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>Status kwestionariusza</span>
            {getStatusBadge(kwestionariush.status)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Utworzony:</span>
              <p className="text-gray-900">{formatDate(kwestionariush.createdAt)}</p>
            </div>
            {kwestionariush.submittedAt && (
              <div>
                <span className="font-medium text-gray-700">Przesłany:</span>
                <p className="text-gray-900">{formatDate(kwestionariush.submittedAt)}</p>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-700">Ostatnia aktualizacja:</span>
              <p className="text-gray-900">{formatDate(kwestionariush.updatedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle>Informacje osobowe</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <DateField label="Data urodzenia" value={personalInfo?.birthDate} />
            <TextField label="Miejsce urodzenia" value={personalInfo?.birthPlace} />
            <TextField label="Adres zameldowania" value={personalInfo?.registrationAddress} />
            <TextField label="Adres zamieszkania na cele podatkowe" value={personalInfo?.taxResidenceAddress} />
            <TextField label="Numer konta bankowego" value={personalInfo?.bankAccountNumber} />
            <TextField label="Urząd Skarbowy" value={personalInfo?.taxOffice} />
            <TextField label="Oddział NFZ" value={personalInfo?.nfzBranch} />
          </div>
        </CardContent>
      </Card>

      {/* Employment Declaration */}
      <Card>
        <CardHeader>
          <CardTitle>Oświadczenie o zatrudnieniu</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <BooleanField label="Jestem zatrudniony/a" value={declarations?.employment?.isEmployed} />
            {declarations?.employment?.isEmployed && (
              <>
                <PeriodField label="Okres zatrudnienia" period={declarations.employment.period} />
                <TextField label="Poziom wynagrodzenia" value={declarations.employment.salaryLevel} />
                <BooleanField label="Jestem na urlopie" value={declarations.employment.onLeave} />
                {declarations.employment.onLeave && (
                  <PeriodField label="Okres urlopu" period={declarations.employment.leavePeriod} />
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Other Contract Declaration */}
      <Card>
        <CardHeader>
          <CardTitle>Oświadczenie o innej umowie</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <BooleanField label="Mam inną umowę" value={declarations?.otherContract?.hasOtherContract} />
            {declarations?.otherContract?.hasOtherContract && (
              <>
                <PeriodField label="Okres umowy" period={declarations.otherContract.period} />
                <BooleanField label="Przekracza płacę minimalną" value={declarations.otherContract.exceedsMinimum} />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Other Insurance Declaration */}
      <Card>
        <CardHeader>
          <CardTitle>Oświadczenie o innym ubezpieczeniu</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <BooleanField label="Mam inne ubezpieczenie" value={declarations?.otherInsurance?.hasOtherInsurance} />
            {declarations?.otherInsurance?.hasOtherInsurance && (
              <TextField label="Tytuł ubezpieczenia" value={declarations.otherInsurance.title} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pension Declaration */}
      <Card>
        <CardHeader>
          <CardTitle>Oświadczenie emerytalne</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <BooleanField label="Jestem emerytem/rencistą" value={declarations?.pension?.isPensioner} />
            {declarations?.pension?.isPensioner && (
              <TextField label="Numer decyzji" value={declarations.pension.decisionNumber} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Disability Declaration */}
      <Card>
        <CardHeader>
          <CardTitle>Oświadczenie o niepełnosprawności</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <BooleanField label="Mam orzeczenie o niepełnosprawności" value={declarations?.disability?.hasDisability} />
            {declarations?.disability?.hasDisability && (
              <>
                <TextField label="Poziom niepełnosprawności" value={declarations.disability.level} />
                <PeriodField label="Ważność orzeczenia" period={declarations.disability.period} />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Status Declarations */}
      <Card>
        <CardHeader>
          <CardTitle>Oświadczenia statusowe</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <BooleanField label="Jestem studentem" value={declarations?.studentStatus} showAsText />
            <BooleanField label="Jestem bezrobotny" value={declarations?.unemploymentStatus} showAsText />
            <BooleanField label="Mam ubezpieczenie społeczne" value={declarations?.socialInsuranceStatus} showAsText />
          </div>
        </CardContent>
      </Card>

      {/* Voluntary Insurance */}
      <Card>
        <CardHeader>
          <CardTitle>Dobrowolne ubezpieczenia</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <BooleanField label="Ubezpieczenie zdrowotne" value={declarations?.voluntaryInsurance?.health} showAsText />
            <BooleanField label="Ubezpieczenie emerytalne" value={declarations?.voluntaryInsurance?.pension} showAsText />
          </div>
        </CardContent>
      </Card>

      {/* Tax Declaration */}
      <Card>
        <CardHeader>
          <CardTitle>Oświadczenia podatkowe</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <BooleanField label="Chcę zwolnienie podatkowe" value={declarations?.taxExemption} showAsText />
            <BooleanField label="Mam certyfikat rezydencji podatkowej" value={declarations?.taxResidencyCertificate?.hasCertificate} />
            {declarations?.taxResidencyCertificate?.hasCertificate && (
              <PeriodField label="Ważność certyfikatu" period={declarations.taxResidencyCertificate.period} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Author Costs */}
      <Card>
        <CardHeader>
          <CardTitle>Koszty autorskie</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <BooleanField label="Przekraczam limit kosztów autorskich" value={declarations?.authorCosts?.exceedsLimit} />
            {declarations?.authorCosts?.exceedsLimit && (
              <TextField label="Kwota kosztów autorskich" value={declarations.authorCosts.amount} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Data Processing Consent */}
      <Card>
        <CardHeader>
          <CardTitle>Zgody</CardTitle>
        </CardHeader>
        <CardContent>
          <BooleanField label="Zgoda na przetwarzanie danych osobowych" value={declarations?.dataProcessingConsent} />
        </CardContent>
      </Card>
    </div>
  );
};